import type { AssignmentSuggestion } from '@chukua/common';
import { usePickerKeyboard } from '../hooks/use-picker-keyboard';
import type { RankedCrews } from '../policies/picker-candidates.policy';
import { CrewRow } from './CrewRow';
import { PickerSection } from './PickerSection';

interface CrewPickerSectionsProps {
  crews: RankedCrews;
  chosenRank: number | null;
  onChoose: (crew: AssignmentSuggestion) => void;
}

/** The two choosable sections, one keyboard list: arrows move across both, Enter chooses. */
export function CrewPickerSections({ crews, chosenRank, onChoose }: CrewPickerSectionsProps) {
  const choosable = [...crews.eligible, ...crews.warning];
  const { activeRank, onKeyDown } = usePickerKeyboard(choosable, onChoose);
  const section = (title: string, items: AssignmentSuggestion[]) =>
    items.length > 0 && (
      <PickerSection title={title} count={items.length}>
        {items.map((crew) => (
          <CrewRow
            key={crew.rank}
            crew={crew}
            isActive={crew.rank === activeRank}
            isChoosing={chosenRank === crew.rank}
            isChoiceLocked={chosenRank !== null}
            onChoose={onChoose}
          />
        ))}
      </PickerSection>
    );

  return (
    <div
      role="group"
      tabIndex={0}
      aria-label="Suggested crews"
      className="flex flex-col gap-4 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      onKeyDown={onKeyDown}
    >
      {section('Eligible', crews.eligible)}
      {section('Eligible with warning', crews.warning)}
    </div>
  );
}
